import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { createPortal } from 'react-dom';
import { useStore, today, fmt, calcLineas, formatEur } from '../store';
import { Btn, Badge, Modal, Input, Select, Textarea, StatCard, SearchBar, useSearch, useLocalStorage, Confirm, ClienteSelector, MultiSelect, useSortable, SortableTh, useT, THEME, pastelBtn, matchArticuloPorCodigo } from './UI';
import { printReparacion, printEntregaReparacion, printEtiquetaReparacion, pdfReparacion, pdfEntregaReparacion } from '../utils/print';
import HistorialSerieRoot from './HistorialSerieRoot';
import { coloresEspacio } from './Misc';
import { useTheme } from '../ThemeContext';

// Mismo esquema de color que los espacios de taller (ver Misc.js), aplicado
// al estado de la reparación en vez de al estado del espacio físico.
// Entregado usa el estilo "libre" (blanco/neutro) a petición del usuario.
function claseFilaReparacion(estado) {
  switch (estado) {
    case 'Recibido': return 'pendiente';
    case 'En diagnóstico': case 'En reparación': return 'enCurso';
    case 'Esperando piezas': return 'piezas';
    case 'Reparado - Listo para recoger': return 'listo';
    case 'Sin reparación - Listo para recoger': return 'sinReparacion';
    case 'Entregado': return 'libre';
    default: return 'otro';
  }
}

// Selector de Lugar: catálogo (Configuración → Espacios taller) + escape a texto
// libre ("Otro") para casos no dados de alta. La key con la que se monta desde
// fuera (form?.id) resetea el modo manual al abrir otra reparación.
function LugarSelector({ valor, disponibles, onChange }) {
  const t = useT();
  const [manual, setManual] = useState(!!valor && !disponibles.includes(valor));
  if (manual) {
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
        <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em' }}>Lugar</label>
        <div style={{ display:'flex', gap:6 }}>
          <input value={valor||''} onChange={e=>onChange(e.target.value)} placeholder="Escribe el lugar..."
            style={{ flex:1, padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, color:t.ink, background:t.surface, outline:'none' }} />
          <button type="button" onClick={() => { setManual(false); onChange(''); }}
            style={{ padding:'7px 10px', borderRadius:8, border:`1px solid ${t.borderStrong}`, background:t.surface, color:t.ink, cursor:'pointer', fontSize:12, whiteSpace:'nowrap' }}>↩ Lista</button>
        </div>
      </div>
    );
  }
  return (
    <Select label="Lugar" value={valor||''} sorted={false}
      options={[{ value:'', label:'— Sin lugar —' }, ...disponibles.map(d => ({ value:d, label:d })), { value:'__otro__', label:'Otro (escribir a mano)' }]}
      onChange={v => { if (v === '__otro__') { setManual(true); onChange(''); } else onChange(v); }} />
  );
}

// Igual que LugarSelector pero para Estado: lista fija + "Otro" para texto
// libre cuando ninguno de los estados normales encaja.
function EstadoSelector({ valor, onChange }) {
  const t = useT();
  const [manual, setManual] = useState(!!valor && !ESTADOS.includes(valor));
  if (manual) {
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
        <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em' }}>Estado</label>
        <div style={{ display:'flex', gap:6 }}>
          <input value={valor||''} onChange={e=>onChange(e.target.value)} placeholder="Escribe el estado..."
            style={{ flex:1, padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, color:t.ink, background:t.surface, outline:'none' }} />
          <button type="button" onClick={() => setManual(false)}
            style={{ padding:'7px 10px', borderRadius:8, border:`1px solid ${t.borderStrong}`, background:t.surface, color:t.ink, cursor:'pointer', fontSize:12, whiteSpace:'nowrap' }}>↩ Lista</button>
        </div>
      </div>
    );
  }
  return (
    <Select label="Estado" value={valor||''} sorted={false}
      options={[...ESTADOS.map(e => ({ value:e, label:e })), { value:'__otro__', label:'Otro (escribir a mano)' }]}
      onChange={v => { if (v === '__otro__') setManual(true); else onChange(v); }} />
  );
}

function AuthImg({ src, ...props }) {
  const theme = useT();
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!src) return;
    let url;
    fetch(src, { headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}` } })
      .then(r => r.blob()).then(b => { url = URL.createObjectURL(b); setBlobUrl(url); });
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [src]);
  return blobUrl ? <img src={blobUrl} {...props} /> : <div style={{ width:'100%', height:'100%', background:theme.surface2 }} />;
}

function descargarImagen(url, filename) {
  fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}` } })
    .then(r => { if (!r.ok) throw new Error('Error ' + r.status); return r.blob(); })
    .then(blob => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    })
    .catch(e => alert('Error descargando imagen: ' + e.message));
}

function termicaDirecta(tipo, rep) {
  return fetch('/api/imprimir-termica', {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ tipo, rep })
  }).then(r => r.json()).then(d => { if (!d.ok) throw new Error(d.error); });
}

function a4Directo(tipo, rep) {
  return fetch('/api/imprimir-a4', {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ tipo, rep })
  }).then(r => r.json()).then(d => { if (!d.ok) throw new Error(d.error); });
}

function enviarEmail(rep, tipo, replaceItemFn, clientesRep) {
  const cli = clientesRep.find(c => String(c.id) === String(rep.clienteId));
  const actual = rep.clienteEmail || cli?.email || '';
  const para = window.prompt(`Enviar email a ${rep.clienteNombre || 'cliente'}:`, actual);
  if (!para || !para.trim()) return;
  const paraFinal = para.trim();
  if (cli && paraFinal !== cli.email) replaceItemFn('clientes', { ...cli, email: paraFinal });
  if (paraFinal !== rep.clienteEmail) replaceItemFn('reparaciones', { ...rep, clienteEmail: paraFinal });
  fetch(`/api/email/reparacion/${rep.id}/${tipo}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ para: paraFinal }),
  }).then(r => r.json()).then(d => {
    if (d.ok) alert(`✅ Email enviado a ${rep.clienteNombre || 'cliente'} (${paraFinal})`);
    else alert('❌ Error: ' + d.error);
  }).catch(e => alert('❌ Error: ' + e.message));
}

function enviarWhatsappRep(rep, tipo, clientesRep) {
  const cli = clientesRep.find(c => String(c.id) === String(rep.clienteId));
  const actual = rep.clienteTel || cli?.telefono || '';
  const tel = window.prompt(`Enviar WhatsApp a ${rep.clienteNombre || 'cliente'}:`, actual);
  if (!tel || !tel.trim()) return;
  let d = tel.trim().replace(/\D/g, '');
  if (d.startsWith('00')) d = d.slice(2);
  if (d.length === 9) d = '34' + d;
  fetch(`/api/whatsapp/reparacion/${rep.id}/${tipo}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ to: d }),
  }).then(r => r.json()).then(r => { if (r.ok) alert(`✅ Enviado por WhatsApp a ${rep.clienteNombre || 'cliente'} (${tel.trim()})`); else alert('❌ ' + r.error); })
  .catch(e => alert('❌ ' + e.message));
}

function enviarFotoWhatsapp(rep, fotoUrl, clientesRep, observacion) {
  const cli = clientesRep.find(c => String(c.id) === String(rep.clienteId));
  const actual = rep.clienteTel || cli?.telefono || '';
  const tel = window.prompt(`Enviar foto a ${rep.clienteNombre || 'cliente'}:`, actual);
  if (!tel || !tel.trim()) return;
  let d = tel.trim().replace(/\D/g, '');
  if (d.startsWith('00')) d = d.slice(2);
  if (d.length === 9) d = '34' + d;
  const msg = window.prompt('Mensaje (edítalo si quieres):', observacion || '');
  if (msg === null) return;
  fetch(`/api/whatsapp/foto-reparacion/${rep.id}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ to: d, fotoUrl, mensaje: msg }),
  }).then(r => r.json()).then(r => { if (r.ok) alert(`✅ Foto enviada por WhatsApp a ${rep.clienteNombre || 'cliente'} (${tel.trim()})`); else alert('❌ ' + r.error); })
  .catch(e => alert('❌ ' + e.message));
}

const ESTADOS = ['Recibido','En diagnóstico','Esperando piezas','En reparación','Reparado - Listo para recoger','Sin reparación - Listo para recoger','Entregado'];
const FILTRO_OTRO = 'Otro';
const TIPOS = ['Portátil','PC sobremesa','Impresora','Tablet','Móvil','Monitor','Otro'];
const URGENCIAS = ['No prioritario','Normal','Urgente','Muy urgente'];
const PAGOS = ['Efectivo','Tarjeta','Bizum','Transferencia'];

function esTelMovil(tel) {
  if (!tel) return false;
  const raw = tel.trim();
  // Número extranjero con prefijo internacional → asumir móvil
  if (raw.startsWith('+') || raw.startsWith('00')) return true;
  const digits = raw.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('34') ? digits.slice(2) : digits;
  return local.length === 9 && /^[67]/.test(local);
}

// Agrupa avisosEspeciales (una entrada por día) en rangos de fechas
// consecutivas y devuelve el primero que empiece entre mañana y dentro de
// 3 días — mismo criterio que el recordatorio automático de PACO en el
// servidor (server/index.js, proximoCierre).
function sumarDias(iso, n) { const [y, m, d0] = iso.split('-').map(Number); const dt = new Date(y, m - 1, d0); dt.setDate(dt.getDate() + n); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; }
function diffDias(a, b) { return Math.round((new Date(a + 'T00:00:00') - new Date(b + 'T00:00:00')) / 86400000); }
function proximoCierre(avisosEspeciales) {
  const fechas = [...new Set((avisosEspeciales || []).map(a => a && a.fecha).filter(Boolean))].sort();
  if (!fechas.length) return null;
  const grupos = [];
  let cur = null;
  for (const f of fechas) {
    if (cur && sumarDias(cur.hasta, 1) === f) cur.hasta = f;
    else { cur = { desde: f, hasta: f }; grupos.push(cur); }
  }
  const hoy = new Date().toISOString().slice(0, 10);
  return grupos.find(g => { const d = diffDias(g.desde, hoy); return d >= 1 && d <= 3; }) || null;
}
function fmtDM(iso) { const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}`; }
function textoRecordatorioCierre(g) {
  return `Recuerda que permaneceremos cerrados desde el ${fmtDM(g.desde)} hasta el ${fmtDM(g.hasta)}.`;
}

function waLink(tel, avisosEspeciales) {
  if (!esTelMovil(tel)) return null;
  let digits = tel.replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  const number = digits.length === 9 ? '34' + digits : digits;
  let msg = `Su ordenador está listo, puede pasar a recogerlo en tienda

Root Informática
Tel. 91 445 88 02

Horario:
L - V :  9.30 a 14.00  y 16.30 a 19.00
Sábados 9.30 a 13.00`;
  const grupo = proximoCierre(avisosEspeciales);
  if (grupo) msg += `\n\n${textoRecordatorioCierre(grupo)}`;
  return `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
}

export function EstadoReparacionBadge({ r, onChange }) {
  const theme = useT();
  const [pos, setPos] = useState(null);
  const [modoOtro, setModoOtro] = useState(false);
  const [otro, setOtro] = useState('');
  const badgeRef = useRef(null);
  const colors = theme === THEME.dark
    ? { 'Recibido':'rgba(59,130,246,0.16)','En diagnóstico':'rgba(245,158,11,0.16)','Esperando piezas':'rgba(167,139,250,0.18)','En reparación':'rgba(59,130,246,0.16)','Reparado - Listo para recoger':'rgba(16,185,129,0.16)','Sin reparación - Listo para recoger':'rgba(239,68,68,0.16)','Entregado':theme.surface2 }
    : { 'Recibido':'#dbeafe','En diagnóstico':'#fef3c7','Esperando piezas':'#ede9fe','En reparación':'#dbeafe','Reparado - Listo para recoger':'#dcfce7','Sin reparación - Listo para recoger':'#fee2e2','Entregado':theme.surface2 };
  const text = theme === THEME.dark
    ? { 'Recibido':'#93c5fd','En diagnóstico':'#fbbf24','Esperando piezas':'#c4b5fd','En reparación':'#93c5fd','Reparado - Listo para recoger':'#6ee7b7','Sin reparación - Listo para recoger':'#fca5a5','Entregado':theme.body }
    : { 'Recibido':'#1e40af','En diagnóstico':'#92400e','Esperando piezas':'#5b21b6','En reparación':'#1e40af','Reparado - Listo para recoger':'#166534','Sin reparación - Listo para recoger':'#991b1b','Entregado':theme.body };

  const abrir = () => {
    const rect = badgeRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    setModoOtro(false);
    setOtro('');
  };

  useEffect(() => {
    if (!pos) return;
    const handler = () => setPos(null);
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pos]);

  return (
    <>
      <span ref={badgeRef} onClick={abrir}
        style={{ display:'inline-block', padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:700, background: colors[r.estado]||theme.surface2, color: text[r.estado]||theme.body, cursor:'pointer', userSelect:'none' }}
        title="Cambiar estado">
        {r.estado} ▾
      </span>
      {pos && modoOtro && createPortal(
        <div onMouseDown={e => e.stopPropagation()}
          style={{ position:'absolute', top: pos.top, left: pos.left, zIndex:9999, background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:10, boxShadow:'0 4px 20px rgba(0,0,0,.15)', minWidth:240, padding:10, display:'flex', flexDirection:'column', gap:8 }}>
          <input autoFocus value={otro} onChange={e=>setOtro(e.target.value)} placeholder="Escribe el estado..."
            style={{ padding:'7px 10px', border:`1px solid ${theme.borderStrong}`, borderRadius:8, fontSize:13, color:theme.ink, background:theme.surface, outline:'none' }} />
          <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
            <button type="button" onClick={() => setModoOtro(false)} style={{ padding:'6px 10px', borderRadius:8, border:`1px solid ${theme.borderStrong}`, background:theme.surface, color:theme.ink, fontSize:12, cursor:'pointer' }}>↩ Lista</button>
            <button type="button" onClick={() => { if (otro.trim()) { setPos(null); onChange(otro.trim()); } }} style={{ padding:'6px 10px', borderRadius:8, border:'none', background:'#1a56db', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer' }}>✓ Aplicar</button>
          </div>
        </div>, document.body
      )}
      {pos && !modoOtro && createPortal(
        <div onMouseDown={e => e.stopPropagation()}
          style={{ position:'absolute', top: pos.top, left: pos.left, zIndex:9999, background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:10, boxShadow:'0 4px 20px rgba(0,0,0,.15)', minWidth:240, overflow:'hidden' }}>
          {ESTADOS.map(e => (
            <div key={e} onClick={() => { setPos(null); if (e !== r.estado) onChange(e); }}
              style={{ padding:'9px 14px', fontSize:13, fontWeight: e===r.estado ? 700 : 400, background: e===r.estado ? colors[e]||theme.surface2 : theme.surface, color: e===r.estado ? text[e]||theme.ink : theme.body, cursor:'pointer', borderBottom:`1px solid ${theme.border}` }}
              onMouseEnter={ev => { if(e!==r.estado) ev.currentTarget.style.background=theme.surface2; }}
              onMouseLeave={ev => { if(e!==r.estado) ev.currentTarget.style.background= e===r.estado ? colors[e] : theme.surface; }}>
              {e === r.estado ? '✓ ' : ''}{e}
            </div>
          ))}
          <div onClick={() => { setModoOtro(true); setOtro(ESTADOS.includes(r.estado) ? '' : r.estado); }}
            style={{ padding:'9px 14px', fontSize:13, fontStyle:'italic', color:theme.muted, cursor:'pointer' }}
            onMouseEnter={ev => { ev.currentTarget.style.background=theme.surface2; }}
            onMouseLeave={ev => { ev.currentTarget.style.background='transparent'; }}>
            ✏️ Otro (escribir a mano)
          </div>
        </div>, document.body
      )}
    </>
  );
}

function statusBadge(e) {
  const m = { 'Recibido':'blue','En diagnóstico':'amber','Esperando piezas':'purple','En reparación':'blue','Reparado - Listo para recoger':'green','Sin reparación - Listo para recoger':'red','Entregado':'gray' };
  return <Badge variant={m[e]||'gray'}>{e}</Badge>;
}

function newRep() {
  return { id:null, num:'', fecha:today(), clienteNombre:'', clienteTel:'', tipoEquipo:'Portátil', equipo:'', sn:'', serieRoot:'', averia:'', accesorios:'', pin:'', presupMax:'', urgencia:'Normal', estado:'Recibido', notas:'', trabajos:'', coste:'', pago:'Tarjeta', fechaEntrega:'', lineasEntrega:[], obsPortal:'', lugar:'Taller' };
}

function LineasEntregaEditor({ lineas, onChange, articulos }) {
  const theme = useT();
  const presets = [
    { label:'Mano de obra', desc:'Mano de obra técnico', precio:35, iva:21 },
    { label:'Desplazamiento', desc:'Desplazamiento técnico', precio:20, iva:21 },
    { label:'Diagnóstico', desc:'Diagnóstico del equipo', precio:15, iva:21 },
  ];
  const add = () => onChange([...lineas, { desc:'', cant:1, precio:0, iva:21 }]);
  const preset = (p) => onChange([...lineas, { desc:p.desc, cant:1, precio:p.precio, iva:p.iva }]);

  // Busca por nombre, ref O código de barras — soporta pistola lectora
  const matchArt = (val) => matchArticuloPorCodigo(articulos, val);
  const selectArt = (i, val) => {
    const a = matchArt(val);
    if (a) onChange(lineas.map((l, idx) => idx === i ? { ...l, desc: a.nombre, precio: a.precio, iva: a.iva } : l));
  };
  const handleKeyDown = (i, e) => {
    if (e.key === 'Enter') {
      const val = e.target.value.trim();
      const a = matchArt(val);
      if (a) {
        onChange(lineas.map((l, idx) => idx === i ? { ...l, desc: a.nombre, precio: a.precio, iva: a.iva } : l));
        e.preventDefault();
        const row = e.target.closest('tr');
        if (row) { const cantInput = row.querySelectorAll('input')[1]; if (cantInput) cantInput.focus(); }
      }
    }
  };
  const upd = (i, f, v) => {
    const next = lineas.map((l, idx) => {
      if (idx !== i) return l;
      if (f === 'ivaIncl') return { ...l, ivaIncl: v };
      if (f === 'iva') {
        const newIva = parseFloat(v) || 0;
        if (l.ivaIncl) {
          const bruto = l.precio * (1 + (l.iva||0)/100);
          return { ...l, iva: newIva, precio: bruto / (1 + newIva/100) };
        }
        return { ...l, iva: newIva };
      }
      if (f === 'precio') {
        if (v === '' || v === '-') return { ...l, precio: v };
        const num = parseFloat(v);
        if (isNaN(num)) return l;
        return { ...l, precio: l.ivaIncl ? num / (1 + (l.iva||0)/100) : num };
      }
      return { ...l, [f]: f==='cant' ? parseFloat(v)||0 : v };
    });
    onChange(next);
  };
  const precioMostrado = (l) => {
    if (typeof l.precio !== 'number') return l.precio;
    return l.ivaIncl ? Math.round(l.precio * (1 + (l.iva||0)/100) * 100) / 100 : l.precio;
  };
  const del = (i) => onChange(lineas.filter((_, idx) => idx !== i));

  return (
    <div>
      <div style={{ display:'flex', gap:6, marginBottom:8, flexWrap:'wrap' }}>
        {presets.map(p => (
          <button key={p.label} onClick={() => preset(p)}
            style={{ background:theme.primaryBg, color:'#1a56db', border:'1px solid #c7d7fc', borderRadius:6, padding:'3px 10px', fontSize:11, fontWeight:600, cursor:'pointer' }}>
            + {p.label}
          </button>
        ))}
      </div>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
        <thead>
          <tr style={{ background:theme.surface2 }}>
            {['Concepto (pieza / trabajo)','Ud','Precio u.','IVA incl.','IVA%','Total',''].map(h => (
              <th key={h} style={{ padding:'6px 8px', textAlign:'left', color:theme.muted, fontSize:11, fontWeight:600, borderBottom:`1px solid ${theme.border}` }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lineas.map((l, i) => (
            <tr key={i}>
              <td style={{ padding:'3px 4px' }}>
                <input list={`arts-rep-${i}`} value={l.desc}
                  onChange={e => { upd(i,'desc',e.target.value); selectArt(i, e.target.value); }}
                  onKeyDown={e => handleKeyDown(i, e)}
                  style={{ width:'100%', padding:'5px 8px', border:`1px solid ${theme.borderStrong}`, borderRadius:6, fontSize:12, outline:'none' }} />
                <datalist id={`arts-rep-${i}`}>
                  {articulos?.map(a => <option key={a.id} value={a.nombre}>{a.ref}{a.barcode ? ` · ${a.barcode}` : ''}</option>)}
                </datalist>
              </td>
              <td style={{ padding:'3px 4px' }}>
                <input type="number" min="0" value={l.cant} onChange={e=>upd(i,'cant',e.target.value)}
                  style={{ width:50, padding:'5px 6px', border:`1px solid ${theme.borderStrong}`, borderRadius:6, fontSize:12, outline:'none' }} />
              </td>
              <td style={{ padding:'3px 4px' }}>
                <input type="number" step="0.01" className="no-spin" value={precioMostrado(l)} onChange={e=>upd(i,'precio',e.target.value)}
                  style={{ width:75, padding:'5px 6px', border:`1px solid ${theme.borderStrong}`, borderRadius:6, fontSize:12, outline:'none' }} />
              </td>
              <td style={{ padding:'3px 4px', textAlign:'center' }}>
                <input type="checkbox" checked={!!l.ivaIncl} onChange={e=>upd(i,'ivaIncl',e.target.checked)} title="El precio introducido ya incluye el IVA" />
              </td>
              <td style={{ padding:'3px 4px' }}>
                <select value={l.iva} onChange={e=>upd(i,'iva',e.target.value)}
                  style={{ padding:'5px 6px', border:`1px solid ${theme.borderStrong}`, borderRadius:6, fontSize:12, outline:'none' }}>
                  {[0,4,10,21].map(v => <option key={v} value={v}>{v}%</option>)}
                </select>
              </td>
              <td style={{ padding:'3px 8px', fontWeight:600, whiteSpace:'nowrap' }}>
                {formatEur(l.cant * l.precio)}
              </td>
              <td style={{ padding:'3px 4px' }}>
                <button onClick={() => del(i)} style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:16 }}>✕</button>
              </td>
            </tr>
          ))}
          {!lineas.length && (
            <tr><td colSpan={7} style={{ padding:'10px 8px', color:theme.faint, fontSize:12, textAlign:'center' }}>
              Usa los botones de arriba o añade línea manual
            </td></tr>
          )}
        </tbody>
      </table>
      <button onClick={add}
        style={{ marginTop:8, background:'none', border:'1px dashed #d1d5db', borderRadius:8, padding:'5px 14px', fontSize:12, color:theme.muted, cursor:'pointer' }}>
        + Añadir línea
      </button>
    </div>
  );
}

// Memoizado aparte: así no se re-renderiza (con sus ~250 filas) en cada
// tecla escrita en el formulario del modal — solo cuando cambian sus props.
const TablaReparaciones = memo(function TablaReparaciones({
  shownItems, sortField, sortDir, handleSort, theme, openEdit, setConfirm, replaceItem, openTicketEntrega, clientes, config, colorFilas, isDark,
}) {
  return (
    <div style={{ background:theme.surface, borderRadius:12, border:`1px solid ${theme.border}`, overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
        <thead>
          <tr style={{ background:theme.surface2 }}>
            {[['Nº','num'],['Entrada','fecha'],['Cliente','clienteNombre'],['Equipo','equipo'],['Lugar','lugar'],['Avería','averia'],['Notas',null],['Estado','estado'],['Acciones',null]].map(([label,field]) => (
              <SortableTh key={label+field} label={label} field={field} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
            ))}
          </tr>
        </thead>
        <tbody>
          {shownItems.map((r, i) => {
            const col = colorFilas ? coloresEspacio(claseFilaReparacion(r.estado), isDark, theme) : null;
            const tcol = col ? col.text : null; // texto claro garantizado sobre fondos oscuros (p.ej. "sin reparación")
            return (
            <tr key={r.id} style={{
              borderBottom:`1px solid ${theme.border}`,
              background: col ? col.bg : (i%2 ? theme.surface2 : 'transparent'),
              backgroundImage: col ? col.img : undefined,
              borderLeft: col ? `${col.bw||3}px solid ${col.border}` : undefined,
              color: tcol || undefined,
            }}>
              <td style={{ padding:'10px 14px', fontWeight:600, color: tcol || theme.primary }}>{r.num}</td>
              <td style={{ padding:'10px 14px' }}>{fmt(r.fecha)}</td>
              <td style={{ padding:'10px 14px' }}>
                <div style={{ fontWeight:500 }}>{r.clienteNombre}</div>
                <div style={{ fontSize:11, color: tcol || theme.faint }}>{r.clienteTel}</div>
              </td>
              <td style={{ padding:'10px 14px', maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.equipo}</td>
              <td style={{ padding:'10px 14px', maxWidth:90, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color: tcol || theme.muted }}>{r.lugar}</td>
              <td title={r.averia} style={{ padding:'10px 14px', color: tcol || theme.muted, maxWidth:160, overflow:'hidden', display:'table-cell', WebkitLineClamp:2, WebkitBoxOrient:'vertical', cursor: r.averia ? 'help' : 'default' }}><div style={{ display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{r.averia}</div></td>
              <td title={r.notas} style={{ padding:'10px 14px', color: tcol || theme.muted, maxWidth:150, cursor: r.notas ? 'help' : 'default' }}><div style={{ display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{r.notas}</div></td>
              <td style={{ padding:'10px 14px' }}>
                <EstadoReparacionBadge r={r} onChange={nuevoEstado => replaceItem('reparaciones', { ...r, estado: nuevoEstado, ...(nuevoEstado === 'Entregado' ? { fechaEntrega: r.fechaEntrega || today(), lugar: '' } : {}) })} />
                {r.urgencia === 'No prioritario' && <span style={{ marginLeft:4, fontSize:10, background:theme.surface2, color:theme.muted, borderRadius:20, padding:'1px 5px', fontWeight:700 }}>🐢 No prioritario</span>}
                {r.urgencia === 'Urgente'        && <span style={{ marginLeft:4, fontSize:10, ...pastelBtn(theme,'red'), borderRadius:20, padding:'1px 5px', fontWeight:700 }}>⚠️ Urgente</span>}
                {r.urgencia === 'Muy urgente'    && <span style={{ marginLeft:4, fontSize:10, background:'#991b1b', color:'#fff',    borderRadius:20, padding:'1px 5px', fontWeight:700 }}>🔴 MUY URGENTE</span>}
              </td>
              <td style={{ padding:'8px 14px', minWidth:220 }}>
                {/* Fila 1 — Comunicación */}
                <div style={{ display:'flex', gap:3, flexWrap:'wrap', marginBottom:3 }}>
                  <Btn size="sm" variant="ghost" onClick={() => openEdit(r)} title="Editar">✏️ Editar</Btn>
                  {waLink(r.clienteTel, config?.avisosEspeciales) && (
                    <a href={waLink(r.clienteTel, config?.avisosEspeciales)} target="_blank" rel="noreferrer" title="WhatsApp al cliente"
                      style={{ display:'inline-flex', alignItems:'center', gap:3, padding:'3px 8px', borderRadius:6, fontSize:11, fontWeight:600, ...pastelBtn(theme,'green'), textDecoration:'none' }}>
                      💬 WA
                    </a>
                  )}
                  {esTelMovil(r.clienteTel) && <Btn size="sm" variant="ghost" title="Enviar enlace del portal por WhatsApp" onClick={async () => { const token = localStorage.getItem('ri_token'); const link = await fetch(`/api/reparaciones/${r.id}/portal-link`, { headers:{ Authorization:'Bearer '+token } }).then(x=>x.json()); const url = link.url; let digits = (r.clienteTel||'').replace(/\D/g,''); if(digits.startsWith('00')) digits=digits.slice(2); const tel = digits.length === 9 ? '34'+digits : digits; const nombre = (r.clienteNombre||'').trim().split(' ')[0]; const msg = `Hola ${nombre}!\nConsulta el estado de tu reparación:\n${url}\nRoot Informática.`; window.open(`https://wa.me/${tel}?text=${encodeURIComponent(msg)}`, '_blank'); }}>🔗 WA</Btn>}
                  {esTelMovil(r.clienteTel) && <Btn size="sm" variant="ghost" title="Pedir contraseña del ordenador por WhatsApp" onClick={() => { let digits = (r.clienteTel||'').replace(/\D/g,''); if(digits.startsWith('00')) digits=digits.slice(2); const tel = digits.length === 9 ? '34'+digits : digits; const nombre = (r.clienteNombre||'').trim().split(' ')[0]; const firma = config?.nombre || 'Root Informática'; const msg = `Buenos días ${nombre}, necesitamos la contraseña del ordenador para continuar y/o testar el trabajo, muchas gracias.\n${firma}`; window.open(`https://wa.me/${tel}?text=${encodeURIComponent(msg)}`, '_blank'); }}>🔑 WA</Btn>}
                  {r.estado !== 'Entregado' && (
                    <Btn size="sm" variant="success" title="Marcar como entregado" onClick={() => replaceItem('reparaciones', { ...r, estado: 'Entregado', fechaEntrega: r.fechaEntrega || today(), lugar: '' })}>📦 Entregar</Btn>
                  )}
                  <Btn size="sm" variant="ghost" style={{ color:'#ef4444' }} onClick={() => setConfirm(r.id)} title="Eliminar">🗑️</Btn>
                </div>
                {/* Filas 2+3 — layout de dos columnas para alinear botones de imprimir */}
                <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                  {/* Fila 2 — Resguardo */}
                  <div style={{ display:'flex', gap:3 }}>
                    <div style={{ display:'flex', gap:3, width:88, flexShrink:0 }}>
                      <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'green') }} onClick={() => termicaDirecta('etiqueta', r).catch(e => { alert('Error impresora: '+e.message+'\n\nAbriendo ventana...'); printEtiquetaReparacion(r, config); })} title="Etiqueta térmica">🏷️</Btn>
                      <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'green') }} title="Etiqueta Brother QL-600" onClick={() => { fetch('/api/etiqueta-ql', { method:'POST', headers:{ Authorization:`Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type':'application/json' }, body: JSON.stringify({ tipo:'rep', num: r.num, cliente: r.clienteNombre, desc: r.equipo||r.averia||'', fecha: r.fecha||'' }) }).then(r=>r.json()).then(d=>{ if(!d.ok) alert('Error: '+d.error); }).catch(e=>alert('Error: '+e.message)); }}>🔖</Btn>
                    </div>
                    <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'blue'), minWidth:92 }} onClick={() => termicaDirecta('resguardo', r).catch(e => { alert('Error impresora: '+e.message+'\n\nAbriendo ventana...'); printReparacion(r, config); })} title="Imprimir resguardo">🖨️ Resguardo</Btn>
                    <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'purple') }} onClick={() => pdfReparacion(r, config)} title="Descargar resguardo PNG">🖼️ PNG</Btn>
                    <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'orange') }} title="Enviar resguardo por email" onClick={() => enviarEmail(r, 'resguardo', replaceItem, clientes)}>✉️ Email</Btn>
                    <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'green') }} title="Enviar resguardo por WhatsApp" onClick={() => enviarWhatsappRep(r, 'resguardo', clientes)}>💬 WA</Btn>
                  </div>
                  {/* Fila 3 — Ticket entrega */}
                  <div style={{ display:'flex', gap:3 }}>
                    <div style={{ display:'flex', gap:3, width:88, flexShrink:0 }}>
                      <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'amber') }} title="Editar ticket de entrega" onClick={() => openTicketEntrega(r)}>📋 Ticket</Btn>
                    </div>
                    {(r.lineasEntrega?.length > 0 || r.coste) && (
                      <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'blue'), minWidth:92 }} onClick={() => termicaDirecta('entrega', r).catch(e => { alert('Error impresora: '+e.message+'\n\nAbriendo ventana...'); printEntregaReparacion(r, config); })} title="Imprimir ticket entrega">🖨️ Entrega</Btn>
                    )}
                    {(r.lineasEntrega?.length > 0 || r.coste) && (
                      <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'purple') }} onClick={() => pdfEntregaReparacion(r, config)} title="Descargar ticket entrega PNG">🖼️ PNG</Btn>
                    )}
                    <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'orange') }} title="Enviar ticket de entrega por email" onClick={() => enviarEmail(r, 'entrega', replaceItem, clientes)}>✉️ Email</Btn>
                    <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'green') }} title="Enviar ticket de entrega por WhatsApp" onClick={() => enviarWhatsappRep(r, 'entrega', clientes)}>💬 WA</Btn>
                  </div>
                </div>
              </td>
            </tr>
            );
          })}
          {!shownItems.length && (
            <tr><td colSpan={7} style={{ padding:30, textAlign:'center', color:theme.faint }}>No hay reparaciones</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
});

// abrirId + onCerrar: modo incrustado (desde Espacios taller). No pinta el listado, solo abre el
// modal de esa reparación y avisa al cerrarlo, para no sacar al usuario de la sección donde está.
export default function Reparaciones({ abrirId = null, abrirTicketId = null, onCerrar = null } = {}) {
  const theme = useT();
  const { data, addItem, replaceItem, deleteItem, nextNum, auth } = useStore();
  const { reparaciones, config, clientes = [], espaciosTaller = [] } = data;
  const puedeVerNotas = auth?.rol === 'admin' || auth?.rol === 'tecnico';
  const { q, setQ, filtered } = useSearch(reparaciones, ['num','clienteNombre','equipo','estado','averia','sn','serieRoot','lugar'], 'reparaciones');
  const { sorted: sortedReps, sortField, sortDir, handleSort } = useSortable(filtered, 'num', 'desc');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [modalFacturaCliente, setModalFacturaCliente] = useState(null); // { rep, nif, direccion, clienteExistente }
  const [modalHistorial, setModalHistorial] = useState(false);
  const [colorFilas, setColorFilas] = useLocalStorage('colorFilasReparaciones', false);
  const { isDark } = useTheme();
  const [filtroEstados, setFiltroEstados] = useLocalStorage('filtroEstados_reparaciones', []);
  const [imprBarcode, setImprBarcode] = useState(false);
  const [fotos, setFotos] = useState([]);
  const [fotosLoading, setFotosLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // índice de foto abierta
  const [webcamOn, setWebcamOn] = useState(false);
  const [webcamError, setWebcamError] = useState('');
  const [webcamSubiendo, setWebcamSubiendo] = useState(false);
  const [webcamContador, setWebcamContador] = useState(0);
  const webcamVideoRef = useRef(null);
  const webcamStreamRef = useRef(null);
  const subirFotoRep = useCallback(async (num, blob, nombre) => {
    const token = localStorage.getItem('ri_token');
    const fd = new FormData();
    fd.append('foto', blob, nombre);
    await fetch(`/api/fotos-rep/${encodeURIComponent(num)}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
  }, []);
  const pararWebcam = useCallback(() => {
    if (webcamStreamRef.current) { webcamStreamRef.current.getTracks().forEach(t => t.stop()); webcamStreamRef.current = null; }
    setWebcamOn(false);
    setWebcamError('');
    setWebcamContador(0);
  }, []);
  useEffect(() => {
    if (!webcamOn) return;
    let cancelado = false;
    navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 } } })
      .then(stream => {
        if (cancelado) { stream.getTracks().forEach(t => t.stop()); return; }
        webcamStreamRef.current = stream;
        if (webcamVideoRef.current) webcamVideoRef.current.srcObject = stream;
      })
      .catch(err => setWebcamError('No se pudo acceder a la cámara: ' + err.message));
    return () => { cancelado = true; };
  }, [webcamOn]);
  const cargarFotos = useCallback(async (num) => {
    if (!num) return;
    setFotosLoading(true);
    try {
      const r = await fetch(`/api/fotos-rep/${encodeURIComponent(num)}`, { headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}` } });
      const d = await r.json();
      setFotos(d.fotos || []);
    } catch {}
    setFotosLoading(false);
  }, []);
  const capturarWebcam = useCallback(() => {
    if (webcamSubiendo) return;
    const video = webcamVideoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(async blob => {
      if (!blob) return;
      setWebcamSubiendo(true);
      await subirFotoRep(form.num, blob, `webcam-${Date.now()}.jpg`);
      await cargarFotos(form.num);
      setWebcamContador(c => c + 1);
      setWebcamSubiendo(false);
    }, 'image/jpeg', 0.85);
  }, [form, webcamSubiendo, subirFotoRep, cargarFotos]);

  useEffect(() => {
    if (modal === 'form' && form?.id && form?.num) cargarFotos(form.num);
    else setFotos([]);
  }, [modal, form?.id, form?.num, cargarFotos]);

  useEffect(() => {
    if (lightbox === null) return;
    const handler = e => {
      if (e.key === 'ArrowRight') setLightbox(i => (i + 1) % fotos.length);
      if (e.key === 'ArrowLeft')  setLightbox(i => (i - 1 + fotos.length) % fotos.length);
      if (e.key === 'Escape')     setLightbox(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, fotos.length]);

  const mesActual = today().slice(0,7);
  const enTaller   = reparaciones.filter(r => r.estado !== 'Entregado');
  const listas     = reparaciones.filter(r => r.estado === 'Reparado - Listo para recoger' || r.estado === 'Sin reparación - Listo para recoger');
  const entregadas = reparaciones.filter(r => r.estado === 'Entregado' && r.fecha.slice(0,7) === mesActual);

  function openNew()      { setForm(newRep()); setModal('form'); }
  // useCallback con deps [] — pasadas como prop a TablaReparaciones (memoizada),
  // necesitan referencia estable para que el memo no se invalide en cada tecla del formulario.
  const openEdit = useCallback(r => { setForm({ ...r, lineasEntrega: r.lineasEntrega || [] }); setModal('form'); }, []);
  const openTicketEntrega = useCallback(r => { setForm({ ...r, lineasEntrega: r.lineasEntrega || [], fechaEntrega: r.fechaEntrega || today(), pago: r.pago || 'Tarjeta' }); setModal('entrega'); }, []);

  // Al llegar desde otra sección (ej. botón "Editar" en Espacios taller) con
  // un id pendiente en sessionStorage, abrir esa reparación directamente.
  useEffect(() => {
    const idPendiente = sessionStorage.getItem('ri_abrir_reparacion_id');
    if (!idPendiente) return;
    sessionStorage.removeItem('ri_abrir_reparacion_id');
    const rep = reparaciones.find(r => String(r.id) === idPendiente);
    if (rep) openEdit(rep);
  }, [reparaciones, openEdit]);

  const modalYaAbierto = useRef(false);
  useEffect(() => {
    if (!abrirId && !abrirTicketId) return;
    const id = abrirId || abrirTicketId;
    const rep = reparaciones.find(x => String(x.id) === String(id));
    if (rep) { if (abrirTicketId) openTicketEntrega(rep); else openEdit(rep); } else if (onCerrar) onCerrar();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al recibir un id nuevo
  }, [abrirId, abrirTicketId]);
  useEffect(() => {
    if (!abrirId && !abrirTicketId) return;
    if (modal) modalYaAbierto.current = true;
    else if (modalYaAbierto.current) { modalYaAbierto.current = false; if (onCerrar) onCerrar(); }
  }, [modal, abrirId, abrirTicketId, onCerrar]);

  function saveEntregaBorrador() {
    replaceItem('reparaciones', { ...form });
    setModal(null);
  }

  function handleNuevoCliente(nuevo) { addItem('clientes', nuevo); }
  function handleEditarCliente(cli) { replaceItem('clientes', cli); }
  function handleClienteChange({ id, nombre, telefono, email }) {
    setForm(f => ({ ...f, clienteId: id, clienteNombre: nombre, clienteTel: telefono||'', clienteEmail: email||'' }));
  }

  function buildRep() {
    if (!form.clienteNombre) { alert('Introduce el nombre del cliente.'); return null; }
    if (!form.equipo)        { alert('Introduce el equipo.'); return null; }

    const formFinal = { ...form };
    // Actualizar email del cliente si ha cambiado (solo su ficha, no reescribe otros documentos)
    if (formFinal.clienteId && formFinal.clienteEmail !== undefined) {
      const cli = clientes.find(c => String(c.id) === String(formFinal.clienteId));
      if (cli && cli.email !== formFinal.clienteEmail) {
        replaceItem('clientes', { ...cli, email: formFinal.clienteEmail });
      }
    }
    if (form.id) {
      replaceItem('reparaciones', formFinal);
      return formFinal;
    } else {
      const n = config.sigReparacion;
      nextNum('sigReparacion');
      const doc = { ...formFinal, id: n, num: String(n) };
      addItem('reparaciones', doc);
      return doc;
    }
  }

  function save() {
    if (buildRep()) setModal(null);
  }

  function saveEntrega() {
    const updated = { ...form, estado:'Entregado', fechaEntrega: form.fechaEntrega || today(), lugar: '' };
    replaceItem('reparaciones', updated);
    setModal(null);
    setTimeout(() => printEntregaReparacion(updated, config), 150);
  }

  function pasarATicketVenta(rep) {
    const lineas = (rep.lineasEntrega||[]).filter(l => l.desc);
    if (!lineas.length) return alert('No hay líneas en el ticket de entrega.');
    const n = config.sigTicket;
    nextNum('sigTicket');
    const num = `T-${String(n).padStart(4,'0')}`;
    const ticket = {
      id: n, num,
      fecha: today(),
      clienteId: rep.clienteId || '',
      clienteNombre: rep.clienteNombre || '',
      clienteTel: rep.clienteTel || '',
      pago: rep.pago || 'Efectivo',
      lineas: lineas.map(l => ({...l})),
      estado: 'Cobrado',
    };
    addItem('tickets', ticket);
    replaceItem('reparaciones', { ...rep, ticketGenerado: num });
    setForm(f => f && f.id === rep.id ? { ...f, ticketGenerado: num } : f);
    setTimeout(() => alert(`✓ Ticket ${num} creado desde reparación ${rep.num}`), 100);
  }

  function pasarAFacturaRep(rep) {
    const lineas = (rep.lineasEntrega||[]).filter(l => l.desc);
    if (!lineas.length) return alert('No hay líneas en el ticket de entrega.');
    const cli = clientes.find(c => String(c.id) === String(rep.clienteId)) || null;
    if (cli && cli.nif && cli.direccion) {
      crearFacturaDesdeRep(rep, cli, cli.nif, cli.direccion);
    } else {
      setModalFacturaCliente({ rep, nif: cli?.nif || '', direccion: cli?.direccion || '', clienteExistente: cli || null });
    }
  }

  function confirmarModalFacturaCliente() {
    const { rep, nif, direccion, clienteExistente } = modalFacturaCliente;
    if (!nif.trim()) return alert('El NIF/CIF es obligatorio para facturar.');
    if (!direccion.trim()) return alert('La dirección de facturación es obligatoria.');
    let cli;
    if (clienteExistente) {
      cli = { ...clienteExistente, nif: nif.trim(), direccion: direccion.trim() };
      replaceItem('clientes', cli);
    } else {
      cli = { id: Date.now(), nombre: rep.clienteNombre || '', nif: nif.trim(), telefono: rep.clienteTel || '', email: '', direccion: direccion.trim() };
      addItem('clientes', cli);
    }
    setModalFacturaCliente(null);
    crearFacturaDesdeRep(rep, cli, nif.trim(), direccion.trim());
  }

  function crearFacturaDesdeRep(rep, clienteFactura, nif, direccion) {
    const lineas = (rep.lineasEntrega||[]).filter(l => l.desc);
    const n = config.sigFactura;
    nextNum('sigFactura');
    const year = new Date().getFullYear();
    const factura = {
      id: n,
      num: `${n}_${year}`,
      tipo: 'FACTURA',
      fecha: today(),
      clienteId: clienteFactura.id,
      clienteNombre: rep.clienteNombre || '',
      clienteTel: rep.clienteTel || clienteFactura.telefono || '',
      clienteNif: nif || '',
      clienteDireccion: direccion || '',
      pago: rep.pago || 'Tarjeta',
      validez: '',
      lineas: lineas.map(l => ({...l})),
      estado: 'Cobrada',
    };
    addItem('facturas', factura);
    replaceItem('reparaciones', { ...rep, facturaGenerada: factura.num });
    setForm(f => f && f.id === rep.id ? { ...f, facturaGenerada: factura.num } : f);
    setTimeout(() => alert(`✓ Factura ${factura.num} creada desde reparación ${rep.num}`), 100);
  }

  const upd = (field, val) => setForm(f => ({
    ...f, [field]: val,
    // Al entregar el equipo el box/estantería queda libre, se limpia solo.
    ...(field === 'estado' && val === 'Entregado' ? { lugar: '' } : {}),
  }));
  // useMemo — referencia estable mientras no cambien datos/filtro/orden, para que
  // TablaReparaciones (memo) no se re-renderice en cada tecla del formulario del modal.
  const shownItems = useMemo(
    () => (!filtroEstados.length ? sortedReps : sortedReps.filter(r => filtroEstados.includes(r.estado) || (filtroEstados.includes(FILTRO_OTRO) && !ESTADOS.includes(r.estado)))),
    [sortedReps, filtroEstados]
  );

  const totEntrega = form
    ? (form.lineasEntrega && form.lineasEntrega.length
        ? calcLineas(form.lineasEntrega)
        : { base: parseFloat(form.coste)||0, iva: (parseFloat(form.coste)||0)*0.21, total: (parseFloat(form.coste)||0)*1.21 })
    : { base:0, iva:0, total:0 };

  return (
    <div>
      {!abrirId && (
        <>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:16 }}>
        <StatCard label="En taller"        value={enTaller.length}   color="#c27803" />
        <StatCard label="Lista recogida"   value={listas.length}     color="#0e9f6e" />
        <StatCard label="Entregadas (mes)" value={entregadas.length} />
        <StatCard label="Total"            value={reparaciones.length} />
      </div>

      <SearchBar value={q} onChange={setQ} placeholder="Buscar por nº, cliente, equipo, avería...">
        <MultiSelect options={[...ESTADOS, FILTRO_OTRO]} selected={filtroEstados} onChange={setFiltroEstados} placeholder="Estado: Todos" />
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:theme.muted, cursor:'pointer', whiteSpace:'nowrap' }}>
          <input type="checkbox" checked={colorFilas} onChange={e => setColorFilas(e.target.checked)} />
          🎨 Colorear por estado
        </label>
        <Btn variant="outline" onClick={() => setModalHistorial(true)} title="Buscar historial por Serie Root o cliente e imprimirlo">🔎 Historial</Btn>
        <Btn variant="primary" onClick={openNew} title="Crear nueva reparación">+ Nueva entrada</Btn>
      </SearchBar>

      <TablaReparaciones
        shownItems={shownItems} sortField={sortField} sortDir={sortDir} handleSort={handleSort} theme={theme}
        openEdit={openEdit} setConfirm={setConfirm} replaceItem={replaceItem} openTicketEntrega={openTicketEntrega}
        clientes={clientes} config={config} colorFilas={colorFilas} isDark={isDark}
      />
        </>
      )}

      {/* ── MODAL HISTORIAL SERIE ROOT ── */}
      {modalHistorial && (
        <Modal title="Historial por Serie Root / Cliente" onClose={() => setModalHistorial(false)} width={520}>
          <HistorialSerieRoot />
        </Modal>
      )}

      {/* ── MODAL NUEVA / EDITAR REPARACIÓN ── */}
      {modal === 'form' && form && (
        <Modal title={form.id ? `Editar ${form.num}` : 'Nueva entrada de taller'} onClose={() => setModal(null)} width={700}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
            {form.id && <Input label="Nº Reparación" value={form.num} readOnly />}
            <Input label="Fecha entrada" type="date" value={form.fecha} onChange={v=>upd('fecha',v)} />
          </div>
          <div style={{ marginBottom:12 }}>
            <ClienteSelector
              clientes={clientes}
              clienteId={form.clienteId}
              clienteNombre={form.clienteNombre}
              onChange={handleClienteChange}
              onNuevoCliente={handleNuevoCliente}
              onEditarCliente={handleEditarCliente}
            />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
            <Input label="Teléfono contacto" value={form.clienteTel||''} onChange={v=>upd('clienteTel',v)} placeholder="Para avisar al terminar" />
            <Input label="Email contacto" value={form.clienteEmail||''} onChange={v=>upd('clienteEmail',v)} placeholder="cliente@email.com" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
            <Select label="Tipo de equipo" value={form.tipoEquipo||'Portátil'} onChange={v=>upd('tipoEquipo',v)} options={TIPOS} />
            <Input label="Marca / Modelo" value={form.equipo} onChange={v=>upd('equipo',v)} placeholder="Ej: HP 15s-fq2000" />
            <Input label="Número de serie" value={form.sn} onChange={v=>upd('sn',v)} placeholder="S/N del equipo" />
            <div style={{ display:'flex', gap:6, alignItems:'flex-end' }}>
              <div style={{ flex:1 }}>
                <Input label="Serie Root" value={form.serieRoot||''} onChange={v=>upd('serieRoot',v)} placeholder="Referencia interna Root" />
              </div>
              <Btn variant="outline" disabled={imprBarcode || !form.serieRoot} title="Imprimir código de barras (Serie Root) en impresora QL"
                onClick={() => {
                  setImprBarcode(true);
                  fetch('/api/etiqueta-ql', { method:'POST', headers:{ Authorization:`Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type':'application/json' },
                    body: JSON.stringify({ tipo:'barcode', desc: form.serieRoot, num: form.num||'' }) })
                    .then(r=>r.json()).then(d=>{ if(!d.ok) alert('Error: '+d.error); })
                    .catch(e=>alert('Error: '+e.message))
                    .finally(()=>setImprBarcode(false));
                }}>🏷️</Btn>
            </div>
            <Input label="Accesorios entregados" value={form.accesorios} onChange={v=>upd('accesorios',v)} placeholder="Cargador, funda..." />
            <Input label="PIN / Contraseña" value={form.pin||''} onChange={v=>upd('pin',v)} placeholder="Solo se imprime en el resguardo" />
            <LugarSelector
              valor={form.lugar}
              disponibles={espaciosTaller.filter(e => !e.fueraDeServicio
                && !reparaciones.some(r => r.id !== form.id && r.estado !== 'Entregado' && r.lugar === e.nombre)).map(e => e.nombre)}
              onChange={v => upd('lugar', v)}
            />
          </div>
          <div style={{ marginBottom:10 }}>
            <Textarea label="Avería declarada por el cliente" value={form.averia} onChange={v=>upd('averia',v)} placeholder="El equipo no arranca, pantalla negra..." />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:10 }}>
            <Input label="Presupuesto aproximado (€)" value={form.presupMax} onChange={v=>upd('presupMax',v)} placeholder="ej: 50-80" />
            <Select label="Urgencia" value={form.urgencia||'Normal'} onChange={v=>upd('urgencia',v)} options={URGENCIAS} />
            <EstadoSelector valor={form.estado} onChange={v=>upd('estado',v)} />
          </div>
          <Textarea label="Trabajos realizados (se imprime en el ticket de entrega)" value={form.trabajos||''} onChange={v=>upd('trabajos',v)} rows={2} placeholder="Cambio de SSD, reinstalación SO, limpieza de polvo..." />
          <div style={{ ...pastelBtn(theme,'blue'), borderRadius:8, padding:'10px 12px' }}>
            <div style={{ fontSize:11, fontWeight:700, color: theme===THEME.dark ? '#93c5fd' : '#1e40af', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:6 }}>🌐 Observaciones visibles en el portal cliente</div>
            <Textarea label="" value={form.obsPortal||''} onChange={v=>upd('obsPortal',v)} rows={2} placeholder="En espera de piezas, pendiente de presupuesto aprobado..." />
          </div>
          {puedeVerNotas && (
            <div style={{ ...pastelBtn(theme,'amber'), borderRadius:8, padding:'10px 12px' }}>
              <div style={{ fontSize:11, fontWeight:700, color: theme===THEME.dark ? '#fbbf24' : '#92400e', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:6 }}>🔒 Notas internas (solo técnicos y admin)</div>
              <Textarea label="" value={form.notas||''} onChange={v=>upd('notas',v)} rows={2} placeholder="Diagnóstico interno, observaciones, piezas pendientes..." />
            </div>
          )}
          {form.id && (
            <div style={{ marginTop:16 }}>
              <div style={{ fontSize:12, fontWeight:700, color:theme.muted, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:8 }}>📷 Fotos del equipo</div>
              {fotosLoading
                ? <div style={{ fontSize:13, color:theme.faint, padding:'8px 0' }}>Cargando...</div>
                : fotos.length === 0
                  ? <div style={{ fontSize:13, color:theme.faint, padding:'8px 0' }}>Sin fotos todavía</div>
                  : <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:8 }}>
                      {fotos.map((f, i) => (
                        <div key={f.nombre} style={{ display:'grid', gridTemplateColumns:'120px 1fr', gap:10, background:theme.surface2, borderRadius:8, padding:8, border:`1px solid ${theme.border}` }}>
                          {/* Miniatura */}
                          <div style={{ position:'relative', borderRadius:6, overflow:'hidden', aspectRatio:'4/3', background:theme.surface2, cursor:'pointer' }} onClick={() => setLightbox(i)}>
                            {f.tipo === 'video'
                              ? <video src={f.url} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} muted />
                              : <AuthImg src={f.url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />}
                            {f.tipo === 'video' && <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}><span style={{ fontSize:28, filter:'drop-shadow(0 1px 3px rgba(0,0,0,.6))' }}>▶</span></div>}
                            <button type="button" onClick={e => { e.stopPropagation(); enviarFotoWhatsapp(form, f.url, clientes, f.observacion); }}
                              style={{ position:'absolute', top:3, left:3, background:'rgba(0,100,0,.7)', border:'none', borderRadius:5, color:'#fff', width:22, height:22, fontSize:11, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }} title="Enviar por WhatsApp">📲</button>
                            <button type="button" onClick={e => { e.stopPropagation(); if(!window.confirm('¿Borrar?')) return; fetch(`/api/fotos-rep/${encodeURIComponent(form.num)}/${encodeURIComponent(f.nombre)}`,{method:'DELETE',headers:{Authorization:`Bearer ${localStorage.getItem('ri_token')}`}}).then(()=>cargarFotos(form.num)); }}
                              style={{ position:'absolute', top:3, right:3, background:'rgba(0,0,0,.6)', border:'none', borderRadius:5, color:'#fff', width:22, height:22, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
                          </div>
                          {/* Controles */}
                          <div style={{ display:'flex', flexDirection:'column', gap:6, justifyContent:'center' }}>
                            <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13, fontWeight:600, color: f.visible ? '#0e9f6e' : '#6b7280' }}>
                              <input type="checkbox" checked={!!f.visible} onChange={e => {
                                const v = e.target.checked;
                                fetch(`/api/fotos-rep/${encodeURIComponent(form.num)}/${encodeURIComponent(f.nombre)}`,{method:'PATCH',headers:{Authorization:`Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type':'application/json'},body:JSON.stringify({visible:v,observacion:f.observacion})})
                                  .then(()=>cargarFotos(form.num));
                              }} style={{ width:16, height:16 }} />
                              Visible en portal cliente
                            </label>
                            <input type="text" placeholder="Observación para el cliente..." value={f.observacion || ''}
                              onChange={e => { setFotos(fs => fs.map((x,j) => j===i ? {...x, observacion:e.target.value} : x)); }}
                              onBlur={e => fetch(`/api/fotos-rep/${encodeURIComponent(form.num)}/${encodeURIComponent(f.nombre)}`,{method:'PATCH',headers:{Authorization:`Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type':'application/json'},body:JSON.stringify({visible:f.visible,observacion:e.target.value})})}
                              style={{ border:`1px solid ${theme.borderStrong}`, borderRadius:6, padding:'5px 8px', fontSize:12, outline:'none' }} />
                          </div>
                        </div>
                      ))}
                    </div>
              }
              <label style={{ display:'inline-block', padding:'7px 14px', border:'1.5px solid #d1d5db', borderRadius:8, fontSize:13, fontWeight:600, color:theme.body, cursor:'pointer', background:theme.surface }}>
                + Añadir foto
                <input type="file" accept="image/*,video/*" multiple style={{ display:'none' }}
                  onChange={async e => {
                    const num = form.num;
                    const token = localStorage.getItem('ri_token');
                    for (const file of Array.from(e.target.files)) {
                      const fd = new FormData();
                      fd.append('foto', file);
                      await fetch(`/api/fotos-rep/${encodeURIComponent(num)}`, { method:'POST', headers:{ Authorization:`Bearer ${token}` }, body:fd });
                    }
                    e.target.value = '';
                    cargarFotos(num);
                  }} />
              </label>
              {' '}
              <button type="button" onClick={() => setWebcamOn(true)}
                style={{ display:'inline-block', padding:'7px 14px', border:'1.5px solid #d1d5db', borderRadius:8, fontSize:13, fontWeight:600, color:theme.body, cursor:'pointer', background:theme.surface }}>
                📹 Cámara web
              </button>
            </div>
          )}
          {webcamOn && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.85)', zIndex:9999, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:14 }}>
              {webcamError
                ? <div style={{ color:'#fff', fontSize:15, maxWidth:360, textAlign:'center' }}>{webcamError}</div>
                : <video ref={webcamVideoRef} autoPlay playsInline muted style={{ maxWidth:'90vw', maxHeight:'70vh', borderRadius:8, background:'#000' }} />}
              {webcamContador > 0 && <div style={{ color:'#fff', fontSize:13, fontWeight:600 }}>✓ {webcamContador} foto{webcamContador===1?'':'s'} guardada{webcamContador===1?'':'s'}</div>}
              <div style={{ display:'flex', gap:10 }}>
                {!webcamError && <Btn variant="ghost" disabled={webcamSubiendo} onClick={capturarWebcam}>{webcamSubiendo ? 'Subiendo...' : '📸 Capturar'}</Btn>}
                <Btn variant="outline" onClick={pararWebcam}>Cerrar</Btn>
              </div>
            </div>
          )}
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
            <Btn variant="outline" onClick={() => setModal(null)} title="Descartar cambios">Cancelar</Btn>
            <Btn variant="ghost" title="Guardar y enviar a impresora térmica" onClick={() => { const doc = buildRep(); if (doc) { setModal(null); setTimeout(() => termicaDirecta('resguardo', doc).catch(e => { alert('Error impresora: '+e.message+'\n\nAbriendo ventana...'); printReparacion(doc, config); }), 150); } }}>🖨️ Guardar e imprimir resguardo</Btn>
            <Btn variant="ghost" style={{ ...pastelBtn(theme,'green') }} title="Guardar y enviar resguardo por WhatsApp" onClick={() => { const doc = buildRep(); if (doc) { setModal(null); enviarWhatsappRep(doc, 'resguardo', clientes); } }}>💬 Guardar y enviar</Btn>
            <Btn variant="primary" onClick={save} title="Guardar sin imprimir">Guardar</Btn>
          </div>
        </Modal>
      )}

      {/* ── MODAL ENTREGA ── */}
      {modal === 'entrega' && form && (
        <Modal title={`Ticket de entrega — ${form.num}`} onClose={() => setModal(null)} width={720}>
          {/* Info reparación */}
          <div style={{ ...pastelBtn(theme,'blue'), borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12 }}>
            <div style={{ display:'flex', gap:24, flexWrap:'wrap' }}>
              <div><span style={{ color:theme.muted }}>Cliente: </span><b style={{ color:theme.ink }}>{form.clienteNombre}</b></div>
              <div><span style={{ color:theme.muted }}>Equipo: </span><b style={{ color:theme.ink }}>{form.equipo}</b></div>
              <div><span style={{ color:theme.muted }}>Entrada: </span><b style={{ color:theme.ink }}>{fmt(form.fecha)}</b></div>
            </div>

            {puedeVerNotas && form.notas && <div style={{ marginTop:4, ...pastelBtn(theme,'amber'), borderRadius:5, padding:'3px 7px', fontSize:12 }}>🔒 {form.notas}</div>}
          </div>

          {/* Trabajos realizados */}
          <Textarea label="Trabajos realizados (se imprime en el ticket)" value={form.trabajos||''} onChange={v=>upd('trabajos',v)} rows={2} placeholder="Cambio de SSD, reinstalación SO, limpieza de polvo..." />

          {/* Fecha entrega + pago */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
            <Input label="Fecha de entrega" type="date" value={form.fechaEntrega||today()} onChange={v=>upd('fechaEntrega',v)} />
            <Select label="Forma de pago" value={form.pago||'Efectivo'} onChange={v=>upd('pago',v)} options={PAGOS} />
          </div>

          {/* Líneas piezas + mano de obra */}
          <div style={{ marginBottom:6, fontSize:13, fontWeight:600, color:theme.body }}>
            Piezas y mano de obra
          </div>
          <div style={{ background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:10, padding:12, marginBottom:12 }}>
            <LineasEntregaEditor lineas={form.lineasEntrega||[]} onChange={l=>upd('lineasEntrega',l)} articulos={data.articulos} />
          </div>

          {/* Totales */}
          <div style={{ display:'flex', justifyContent:'flex-end', gap:24, padding:'8px 0', borderTop:`1px solid ${theme.border}`, fontSize:13 }}>
            <span style={{ color:theme.muted }}>Base: <b style={{ color:theme.ink }}>{formatEur(totEntrega.base)}</b></span>
            <span style={{ color:theme.muted }}>IVA: <b style={{ color:theme.ink }}>{formatEur(totEntrega.iva)}</b></span>
            <span style={{ fontSize:16, fontWeight:700, color:theme.primary }}>Total: {formatEur(totEntrega.total)}</span>
          </div>

          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12, flexWrap:'wrap', alignItems:'center' }}>
            {(form.facturaGenerada || form.ticketGenerado) && (
              <span style={{ fontSize:11, ...pastelBtn(theme,'green'), borderRadius:20, padding:'3px 9px', fontWeight:700 }}>
                {form.facturaGenerada || form.ticketGenerado}
              </span>
            )}
            {!form.facturaGenerada && !form.ticketGenerado && (
              <Btn variant="success" style={{ background:'#0e9f6e' }} onClick={() => pasarATicketVenta(form)} title="Crear ticket de venta a partir de este ticket de entrega">🧾→T Ticket</Btn>
            )}
            {!form.facturaGenerada && !form.ticketGenerado && (
              <Btn variant="success" onClick={() => pasarAFacturaRep(form)} title="Crear factura a partir de este ticket de entrega">📄→F Factura</Btn>
            )}
            <Btn variant="outline" onClick={() => setModal(null)} title="Descartar cambios">Cancelar</Btn>
            <Btn variant="ghost" onClick={saveEntregaBorrador} title="Guardar ticket sin imprimir">💾 Guardar</Btn>
            <Btn variant="ghost" title="Guardar e imprimir ticket de entrega" onClick={() => { replaceItem('reparaciones', { ...form }); termicaDirecta('entrega', form).catch(e => { alert('Error impresora: '+e.message+'\n\nAbriendo ventana...'); printEntregaReparacion(form, config); }); setModal(null); }}>🖨️ Guardar e imprimir</Btn>
            <Btn variant="ghost" style={{ ...pastelBtn(theme,'green') }} title="Guardar, imprimir y enviar por WhatsApp" onClick={async () => {
              await replaceItem('reparaciones', { ...form });
              try { await termicaDirecta('entrega', form); } catch(e) { alert('Error impresora: '+e.message+'\n\nAbriendo ventana...'); printEntregaReparacion(form, config); }
              enviarWhatsappRep(form, 'entrega', clientes);
              setModal(null);
            }}>💬 Guardar, imprimir y WA</Btn>
            {form.estado !== 'Entregado' && (
              <Btn variant="success" onClick={saveEntrega}>📦 Marcar como entregado</Btn>
            )}
          </div>
        </Modal>
      )}

      {confirm && (
        <Confirm msg="¿Eliminar esta reparación?" onOk={() => { deleteItem('reparaciones', confirm); setConfirm(null); }} onCancel={() => setConfirm(null)} />
      )}

      {modalFacturaCliente && (
        <Modal title="Completar datos de facturación" onClose={() => setModalFacturaCliente(null)} width={420}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div style={{ fontSize:13, color:theme.muted }}>
              Para facturar a <b>{modalFacturaCliente.rep.clienteNombre || 'este cliente'}</b> hace falta el NIF/CIF y la dirección de facturación.
              {modalFacturaCliente.clienteExistente ? ' Ya está en clientes de facturación, solo falta completar.' : ' Se creará como nuevo cliente de facturación.'}
            </div>
            <Input label="NIF / CIF" value={modalFacturaCliente.nif} onChange={v=>setModalFacturaCliente(m=>({...m, nif:v}))} />
            <Input label="Dirección de facturación" value={modalFacturaCliente.direccion} onChange={v=>setModalFacturaCliente(m=>({...m, direccion:v}))} />
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
              <Btn variant="outline" onClick={() => setModalFacturaCliente(null)}>Cancelar</Btn>
              <Btn variant="success" onClick={confirmarModalFacturaCliente}>Crear factura</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* ── LIGHTBOX ── */}
      {lightbox !== null && fotos.length > 0 && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.92)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
          {/* Botón anterior */}
          {fotos.length > 1 && (
            <button type="button" onClick={e => { e.stopPropagation(); setLightbox((lightbox - 1 + fotos.length) % fotos.length); }}
              style={{ position:'absolute', left:16, background:'rgba(255,255,255,.15)', border:'none', borderRadius:'50%', color:'#fff', width:44, height:44, fontSize:22, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>‹</button>
          )}
          {/* Imagen o vídeo */}
          {fotos[lightbox].tipo === 'video'
            ? <video src={fotos[lightbox].url} controls autoPlay onClick={e => e.stopPropagation()}
                style={{ maxWidth:'90vw', maxHeight:'90vh', borderRadius:8, boxShadow:'0 8px 40px rgba(0,0,0,.6)' }} />
            : <AuthImg src={fotos[lightbox].url} alt="" onClick={e => e.stopPropagation()}
                style={{ maxWidth:'90vw', maxHeight:'90vh', objectFit:'contain', borderRadius:8, boxShadow:'0 8px 40px rgba(0,0,0,.6)' }} />}
          {/* Botón siguiente */}
          {fotos.length > 1 && (
            <button type="button" onClick={e => { e.stopPropagation(); setLightbox((lightbox + 1) % fotos.length); }}
              style={{ position:'absolute', right:16, background:'rgba(255,255,255,.15)', border:'none', borderRadius:'50%', color:'#fff', width:44, height:44, fontSize:22, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>›</button>
          )}
          {/* Contador y cerrar */}
          <div style={{ position:'absolute', top:16, left:0, right:0, display:'flex', justifyContent:'center' }}>
            <span style={{ background:'rgba(0,0,0,.5)', color:'#fff', borderRadius:20, padding:'4px 14px', fontSize:13 }}>{lightbox + 1} / {fotos.length}</span>
          </div>
          <button type="button" onClick={() => setLightbox(null)}
            style={{ position:'absolute', top:16, right:16, background:'rgba(255,255,255,.15)', border:'none', borderRadius:'50%', color:'#fff', width:36, height:36, fontSize:18, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
        </div>
      )}
    </div>
  );
}
